import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, HashRouter } from 'react-router-dom';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './context/AuthContext';
import { ToastContainer, showToast } from './components/Toast';
import { ConfirmDialogProvider } from './components/ConfirmDialog';
import App from './App';
import './index.css';

// HashRouter is used in the Electron desktop build (file:// URLs don't support
// HTML5 history API). BrowserRouter is used for the web deployment.
const Router = import.meta.env.VITE_USE_HASH_ROUTER === 'true' ? HashRouter : BrowserRouter;

function onQueryError(error: unknown) {
  const msg = error instanceof Error ? error.message : 'Something went wrong';
  if (msg === 'Session expired') return; // client.ts handles 401 → redirect
  showToast(msg);
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onQueryError }),
  mutationCache: new MutationCache({ onError: onQueryError }),
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30 * 1000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Router>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <App />
          <ToastContainer />
          <ConfirmDialogProvider />
        </AuthProvider>
      </QueryClientProvider>
    </Router>
  </React.StrictMode>,
);
